import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  Clock,
  Eye,
  FileText,
  History,
  Loader,
  PenLine,
  RotateCcw,
  Send,
  Wrench,
} from 'lucide-react';
import { TermsService } from '../../services/termsService';
import { TermsModel } from '../../types/terms';
import { highestVersion, nextPatchVersion } from '../../helpers/termsVersion';
import TermsEditor from './TermsEditor';
import PublishTermsDialog from './PublishTermsDialog';
import './termsEditor.css';

/**
 * TipTap represents an empty document as "<p></p>", which is not the same as an
 * empty string, so emptiness is judged on the visible text.
 */
const isBlankHtml = (html: string): boolean =>
  html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;

const formatTimestamp = (timestamp: Timestamp | null): string => {
  if (!timestamp) return '—';

  return timestamp.toDate().toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TermsSection = () => {
  const [history, setHistory] = useState<TermsModel[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [html, setHtml] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Bumped whenever the body is replaced from outside the editor. Selecting a
  // version is not enough on its own: discarding edits to the version already
  // loaded leaves the id unchanged, and without this the editor would keep
  // displaying the text that was just thrown away.
  const [editorSeed, setEditorSeed] = useState(0);

  const loadTerms = useCallback(async () => {
    try {
      setIsLoading(true);
      const terms = await TermsService.loadTermsHistory();
      setHistory(terms);
      setSelectedId(terms[0]?.id ?? null);
      setHtml(terms[0]?.htmlContent ?? '');
      setEditorSeed((seed) => seed + 1);
    } catch (error) {
      console.error('Error loading terms:', error);
      toast.error('Failed to load terms & conditions', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  // The doc the mobile app currently serves: newest by updatedAt, which is the
  // ordering loadTermsHistory already applies.
  const liveTerms = history[0] ?? null;
  const currentVersion = useMemo(
    () => highestVersion(history.map((terms) => terms.version)),
    [history]
  );
  const nextVersion = useMemo(
    () => nextPatchVersion(currentVersion),
    [currentVersion]
  );

  const selectedTerms = history.find((terms) => terms.id === selectedId) ?? null;
  const isViewingOldVersion = selectedTerms !== null && selectedTerms.id !== liveTerms?.id;
  const legacyCount = history.filter((terms) => terms.createdAt === null).length;

  const hasChanges = html !== (liveTerms?.htmlContent ?? '');
  const canPublish = hasChanges && !isBlankHtml(html);

  const handleSelectVersion = (terms: TermsModel) => {
    setSelectedId(terms.id);
    setHtml(terms.htmlContent);
    setShowPreview(false);
    setEditorSeed((seed) => seed + 1);
  };

  const handleDiscardChanges = () => {
    setSelectedId(liveTerms?.id ?? null);
    setHtml(liveTerms?.htmlContent ?? '');
    setEditorSeed((seed) => seed + 1);
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const published = await TermsService.publishNewVersion(html);
      await loadTerms();
      setShowPublishDialog(false);

      toast.success(`Published version ${published.version}`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error publishing terms:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to publish terms',
        { position: 'top-right', autoClose: 5000 }
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const handleBackfill = async () => {
    setIsBackfilling(true);
    try {
      const updated = await TermsService.backfillCreatedAt();
      await loadTerms();

      toast.success(`Added a created date to ${updated} version(s)`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error backfilling created dates:', error);
      toast.error('Failed to add created dates', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="size-6 animate-spin text-custom-500" />
        <span className="ml-2 text-slate-500">Loading terms &amp; conditions...</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h6 className="text-15 font-semibold">Terms &amp; Conditions</h6>
          <p className="text-sm text-slate-500 dark:text-zink-400 mt-1">
            Publishing a version re-prompts every user to accept.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="px-2 py-1 text-[11px] rounded-full border border-orange-200 bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:border-orange-500/30">
              Unsaved changes
            </span>
          )}

          <button
            onClick={() => setShowPreview((previous) => !previous)}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors flex items-center"
          >
            {showPreview ? (
              <>
                <PenLine className="size-4 mr-2" />
                Edit
              </>
            ) : (
              <>
                <Eye className="size-4 mr-2" />
                Preview
              </>
            )}
          </button>

          {hasChanges && (
            <button
              onClick={handleDiscardChanges}
              disabled={isPublishing}
              className="px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors flex items-center disabled:opacity-50"
            >
              <RotateCcw className="size-4 mr-2" />
              Discard
            </button>
          )}

          <button
            onClick={() => setShowPublishDialog(true)}
            disabled={!canPublish || isPublishing}
            title={
              canPublish
                ? `Publish as ${nextVersion}`
                : 'Make a change to publish a new version'
            }
            className="px-4 py-2 text-sm bg-custom-500 hover:bg-custom-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Send className="size-4 mr-2" />
            Publish {nextVersion}
          </button>
        </div>
      </div>

      {legacyCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 rounded-md bg-slate-50 dark:bg-zink-700 border border-slate-200 dark:border-zink-500">
          <span className="text-sm text-slate-600 dark:text-zink-200">
            {legacyCount} older version(s) have no created date. They can be
            stamped from their last-updated time.
          </span>
          <button
            onClick={handleBackfill}
            disabled={isBackfilling}
            className="px-3 py-1.5 text-sm border border-slate-300 dark:border-zink-500 rounded-md hover:bg-white dark:hover:bg-zink-600 transition-colors flex items-center disabled:opacity-50"
          >
            {isBackfilling ? (
              <Loader className="size-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="size-4 mr-2" />
            )}
            Add created dates
          </button>
        </div>
      )}

      {isViewingOldVersion && (
        <div className="mb-4 p-3 rounded-md text-sm border border-orange-200 bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-500">
          You loaded version {selectedTerms?.version}, which is not the live
          one. Publishing will copy this text forward as {nextVersion} — it does
          not restore the old version in place.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          {/* The editor is seeded from `html` rather than from the stored
              doc, so edits made before flipping to Preview survive the
              unmount that toggling causes. */}
          {showPreview ? (
            // Mirrors the editor's shape — bordered box, header strip, then a
            // body of the same fixed height — so toggling does not resize the
            // column.
            <div className="border border-slate-200 dark:border-zink-500 rounded-md overflow-hidden">
              <div className="flex items-center p-2 border-b border-slate-200 dark:border-zink-500">
                <span className="px-2 py-1.5 text-sm text-slate-500 dark:text-zink-400 flex items-center">
                  <Eye className="size-4 mr-2" />
                  Preview — as the app renders it
                </span>
              </div>
              <div className="h-[420px] overflow-y-auto px-4 py-3">
                {isBlankHtml(html) ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <FileText className="size-10 mb-2" />
                    <p className="text-sm">Nothing to preview yet</p>
                  </div>
                ) : (
                  <div
                    className="terms-rich-text"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )}
              </div>
            </div>
          ) : (
            <TermsEditor
              content={html}
              contentKey={`${selectedId ?? 'empty'}-${editorSeed}`}
              onChange={setHtml}
              disabled={isPublishing}
            />
          )}

          <p className="text-xs text-slate-500 dark:text-zink-400 mt-2">
            Formatting is limited to what the mobile app renders: headings,
            paragraphs, bold, italic and lists.
          </p>
        </div>

        <div className="border border-slate-200 dark:border-zink-500 rounded-md overflow-hidden">
          <div className="flex items-center justify-between p-2 border-b border-slate-200 dark:border-zink-500">
            <span className="px-2 py-1.5 text-sm font-semibold flex items-center">
              <History className="size-4 mr-2" />
              Version history
            </span>
            <span className="px-2 text-xs text-slate-500 dark:text-zink-400">
              {history.length}
            </span>
          </div>

          {history.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-zink-400 px-4 py-3">
              No versions published yet.
            </p>
          ) : (
            <ul className="space-y-2 h-[420px] overflow-y-auto p-2">
              {history.map((terms) => {
                const isLive = terms.id === liveTerms?.id;
                const isSelected = terms.id === selectedId;

                return (
                  <li key={terms.id}>
                    <button
                      onClick={() => handleSelectVersion(terms)}
                      className={`w-full text-left p-3 rounded-md border transition-colors ${
                        isSelected
                          ? 'border-custom-200 bg-custom-50 dark:bg-custom-500/10 dark:border-custom-500/30'
                          : 'border-slate-200 dark:border-zink-500 hover:bg-slate-50 dark:hover:bg-zink-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-800 dark:text-zink-100">
                          {terms.version || 'unversioned'}
                        </span>
                        {isLive && (
                          <span className="px-2 py-0.5 text-[11px] rounded-full border border-green-200 bg-green-50 text-green-500 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-500">
                            Live
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-xs text-slate-500 dark:text-zink-400">
                        <Clock className="size-3 mr-1.5 shrink-0" />
                        Created {formatTimestamp(terms.createdAt)}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <PublishTermsDialog
        show={showPublishDialog}
        currentVersion={currentVersion}
        nextVersion={nextVersion}
        isPublishing={isPublishing}
        onClose={() => setShowPublishDialog(false)}
        onConfirm={handlePublish}
      />
    </React.Fragment>
  );
};

export default TermsSection;
